import { ProcessedRuleset, CustomerWorkspace } from '../types';
import { makeBackendRequest, makeBackendBlobRequest } from './tokenService';

// Rulesets API now returns array directly
type RulesetsResponse = ProcessedRuleset[];

interface YamlResponse {
  yaml: string;
  errors?: Array<{
    message: string;
  }>;
}

interface WorkspaceOverwritesResponse {
  filename: string;
  excelBuffer: any;
}

// Fetch rulesets from backend (which tunnels to Recyda API)
export const fetchRulesets = async (): Promise<{ rulesets: ProcessedRuleset[] }> => {
  try {
    console.log('🔍 Fetching rulesets via backend...');

    const rulesets: RulesetsResponse = await makeBackendRequest('/rulesets', {
      method: 'GET',
    });

    console.log('✅ Rulesets fetched via backend');

    // Backend now returns array directly
    if (!Array.isArray(rulesets)) {
      throw new Error('Invalid response format - expected array');
    }

    return {
      rulesets: rulesets
    };
  } catch (error) {
    console.error('❌ Error fetching rulesets via backend:', error);
    throw error;
  }
};

// Fetch YAML code from backend (which tunnels to Recyda API)
export const fetchYamlCode = async (rulesetKey: string): Promise<string> => {
  try {
    console.log(`🔍 Fetching YAML via backend for ruleset: ${rulesetKey}`);

    const response: YamlResponse = await makeBackendRequest('/yaml?rulesetKey=' + rulesetKey, {
      method: 'GET',
    });

    console.log('✅ YAML fetched via backend');

    if (response.errors) {
      throw new Error(response.errors[0]?.message || 'GraphQL errors occurred');
    }

    if (!response.yaml) {
      throw new Error('No data received from API');
    }

    return response.yaml || '';
  } catch (error) {
    console.error('❌ Error fetching YAML via backend:', error);
    throw error;
  }
};

// Fetch customer workspaces from backend
export const fetchCustomerWorkspaces = async (): Promise<CustomerWorkspace[]> => {
  try {
    console.log('🔍 Fetching customer workspaces via backend...');

    const response: CustomerWorkspace[] = await makeBackendRequest('/customer-workspaces', {
      method: 'GET',
    });

    console.log('✅ Customer workspaces fetched via backend');

    if (!response) {
      throw new Error('Invalid response format - expected workspaces array');
    }

    return response;
  } catch (error) {
    console.error('❌ Error fetching customer workspaces via backend:', error);
    throw error;
  }
};

// Fetch workspace overwrites from backend
export const fetchWorkspaceOverwrites = async (workspaceKey: string): Promise<WorkspaceOverwritesResponse> => {
  try {
    console.log(`🔍 Fetching workspace overwrites via backend for workspace: ${workspaceKey}`);

    const response = await makeBackendBlobRequest(`/workspace-overwrites-audit-report?workspaceKey=${workspaceKey}`, {
      method: 'GET',
    });

    console.log('✅ Workspace overwrites fetched via backend');

    // Extract filename from Content-Disposition header
    const contentDisposition = response.headers.get('content-disposition');
    let filename = `workspace-heuristics-audit-${workspaceKey}.xlsx`;
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename="(.+)"/);
      if (filenameMatch) {
        filename = filenameMatch[1];
      }
    }

    // Convert response to array buffer
    const arrayBuffer = await response.arrayBuffer();

    return {
      filename,
      excelBuffer: arrayBuffer
    };
  } catch (error) {
    console.error('❌ Error fetching workspace overwrites via backend:', error);
    throw error;
  }
};